import React, { forwardRef, useCallback } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useLenis } from 'lenis/react';

type Variant = 'fill' | 'transparent';

type CommonProps = {
  variant?: Variant;
  className?: string;
  children: React.ReactNode;
  /**
   * Forcer le comportement \"ancre\" (scroll natif jusqu'à l'id).
   * Utilisé pour les href de type '/#section'.
   */
  isAnchor?: boolean;
  /**
   * Permet d'accéder à `target` sur le type commun tout en
   * le réservant logiquement aux liens (voir ButtonAsButton ci‑dessous).
   */
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
};

type ButtonAsButton = CommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    // On interdit explicitement target côté bouton pour éviter les confusions
    target?: undefined;
  };

type ButtonAsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    href: string;
    external?: boolean;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const baseClasses =
  'inline-flex items-center justify-center rounded-full border-2 border-orange px-7.5 py-1.5 text-md transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2';

const variantClasses: Record<Variant, string> = {
  fill: 'bg-orange text-white hover:bg-transparent hover:text-orange',
  transparent: 'bg-transparent text-orange hover:bg-orange hover:text-white',
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    { variant = 'fill', className = '', children, href, target, isAnchor = false, ...rest },
    ref,
  ) => {
    const classes = clsx(baseClasses, variantClasses[variant], className);

    const isLink = typeof href === 'string';
    const isExternal = (isLink && /^https?:\/\//.test(href)) || target === '_blank';

    const router = useRouter();
    const lenis = useLenis();

    const handleAnchorClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!href) return;

        const url = new window.URL(href, window.location.origin);
        const hash = url.hash.replace('#', '');
        const path = url.pathname || '/';

        if (!hash) return;

        event.preventDefault();

        const scrollToHash = () => {
          const element = document.getElementById(hash);
          if (!element) return;

          const headerOffset = 96; // hauteur approximative du header (h-24)
          const targetY = element.getBoundingClientRect().top + window.scrollY - headerOffset;

          if (lenis) {
            lenis.scrollTo(targetY, { immediate: false });
          } else {
            window.scrollTo({ top: targetY, behavior: 'smooth' });
          }
        };

        if (router.pathname !== path) {
          router.push(href, undefined, { scroll: false });
          return;
        }

        scrollToHash();
      },
      [href, lenis, router],
    );

    if (isAnchor) {
      const { onClick, ...anchorRest } = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;

      const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleAnchorClick(event);
      };

      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={className}
          href={href!}
          target={target}
          {...anchorRest}
          onClick={handleClick}
        >
          {children}
        </a>
      );
    }

    if (isLink) {
      if (isExternal || target === '_blank') {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={classes}
            href={href}
            rel={target === '_blank' ? 'noreferrer' : undefined}
            target={target}
            {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {children}
          </a>
        );
      }

      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          href={href}
          scroll={false}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
